import type { PublicClientApplication } from "@azure/msal-browser";
import { App } from "@capacitor/app";
import { AuthBrowser, consumeRedirectResult, clearMsalInteractionLock } from "./msal";
import { isNativeShell } from "./platform";
import {
  isNativeAuthRedirect,
  msalResponseHash,
  oauthRedirectError,
  oauthRedirectHasAuthCode,
} from "./redirect";
import { grantNativeSession, hasNativeSession, revokeNativeSession } from "./session";

export const NATIVE_LOGIN_ERROR_KEY = "ironfleet.nativeLoginError";

let inflight: Promise<void> | undefined;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function rejectNativeLogin(pca: PublicClientApplication, message: string): Promise<void> {
  try {
    await pca.clearCache();
  } catch {
    // Ignore empty cache.
  }
  pca.setActiveAccount(null);
  sessionStorage.setItem(NATIVE_LOGIN_ERROR_KEY, message);
}

async function applyRedirectUrl(pca: PublicClientApplication, url: string): Promise<void> {
  if (!isNativeAuthRedirect(url)) {
    return;
  }
  if (inflight) {
    await inflight;
    return;
  }
  inflight = (async () => {
    const oauthError = oauthRedirectError(url);
    if (oauthError) {
      clearMsalInteractionLock();
      await rejectNativeLogin(pca, oauthError);
      revokeNativeSession();
      return;
    }
    if (!oauthRedirectHasAuthCode(url)) {
      return;
    }
    const hash = msalResponseHash(url);
    try {
      const result = await consumeRedirectResult(pca, hash);
      if (result?.idToken && result.account) {
        pca.setActiveAccount(result.account);
        sessionStorage.removeItem(NATIVE_LOGIN_ERROR_KEY);
        grantNativeSession();
        try {
          await AuthBrowser.close();
        } catch {
          // Tab may already be gone.
        }
        return;
      }
      clearMsalInteractionLock();
      await rejectNativeLogin(pca, "Microsoft sign-in did not return a token. Try again.");
      revokeNativeSession();
    } catch (error) {
      clearMsalInteractionLock();
      const message = error instanceof Error && error.message.trim() ? error.message : "Microsoft sign-in failed.";
      await rejectNativeLogin(pca, message);
      revokeNativeSession();
    }
  })().finally(() => {
    inflight = undefined;
    window.dispatchEvent(new Event("ironfleet-auth-settled"));
  });
  await inflight;
}

/** Deep-link Microsoft's auth code back into MSAL after Custom Tabs. Never block first paint. */
export async function wireNativeMsalRedirect(pca: PublicClientApplication): Promise<void> {
  if (!isNativeShell()) {
    return;
  }
  const onUrl = (url?: string) => {
    if (url) {
      void applyRedirectUrl(pca, url);
    }
  };
  try {
    await AuthBrowser.addListener("redirect", ({ url }) => onUrl(url));
  } catch (error) {
    console.error(error);
  }
  try {
    await App.addListener("appUrlOpen", ({ url }) => onUrl(url));
  } catch (error) {
    console.error(error);
  }
  try {
    await App.addListener("appStateChange", ({ isActive }) => {
      if (!isActive) {
        return;
      }
      void delay(900).then(() => {
        if (!hasNativeSession() && !inflight) {
          window.dispatchEvent(new Event("ironfleet-auth-settled"));
        }
      });
    });
  } catch (error) {
    console.error(error);
  }
  try {
    const pending = await AuthBrowser.consumePending();
    onUrl(pending?.url);
  } catch (error) {
    console.error(error);
  }
  try {
    const launch = await Promise.race([
      App.getLaunchUrl(),
      delay(1500).then(() => undefined),
    ]);
    onUrl(launch?.url);
  } catch (error) {
    console.error(error);
  }
}
